import React, { useCallback, useEffect, useState, useRef } from "react";
import { GameOverType, GTVMessage, PlayerType } from "../../shared/util/types";
import { AllGuessesInType, NewQuestionType, NextRoundType } from "./types";
import ShowScores from "../../shared/components/ShowScores";
import MultipleChoice from "../../shared/components/MultipleChoice";
import TransitionContainer from "../../shared/components/TransitionContainer";
import { DEFAULT_THEME } from "../../shared/util/consts";
import { Box } from "@mantine/core";
import DynamicText from "../../shared/components/DynamicText";
import PlayerReminder from "../../shared/components/PlayerReminder";

const TriviaGame = () => {
  const delayShowingAnswersMS = 10000;
  const [players, setPlayers] = useState<PlayerType[]>([]);
  const theme = DEFAULT_THEME;
  const sendHostMessage = useCallback(async (msg: string) => {}, []);
  const [round, setRound] = useState(1);
  const [instructions, setInstructions] = useState("");
  const [reminder, setReminder] = useState("");
  const [state, setState] = useState("NextRound"); // Should be NextRound to start
  const [cards, setCards] = useState<string[]>([]);
  const [totalPlayersSubmitted, setTotalPlayersSubmitted] = useState(0);
  const setPlayersUnsubmitted = useCallback(() => {
    setPlayers(
      players.map((p) => {
        return {
          ...p,
          Submitted: false,
        };
      })
    );
  }, [players, setPlayers]);
  const onLoaded = async () => {
    await sendHostMessage("GameStarted");
  };
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      onLoaded();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("Trivia Processing msg", msg);
      if (!msg) {
        return false;
      }
      switch (msg.Type) {
        case "AddPlayer":
          const newPlayer = msg.Payload as PlayerType;
          setPlayers([...players, { ...newPlayer, Submitted: true }]);
          break;
        case "NextRound":
          setState(msg.Type);
          const nextRound: NextRoundType = msg.Payload as NextRoundType;
          setRound(nextRound.Round);
          setInstructions(nextRound.Instructions);
          setReminder(nextRound.Reminder);
          setCards(nextRound.Categories);
          setTotalPlayersSubmitted(-1);
          setPlayersUnsubmitted();
          await sendHostMessage("ShowInstructionsComplete");
          break;
        case "NewQuestion":
          setState(msg.Type);
          const newQuestion: NewQuestionType = msg.Payload as NewQuestionType;
          setRound(newQuestion.Round);
          setInstructions(newQuestion.Instructions);
          setReminder(newQuestion.Reminder);
          setCards(newQuestion.AnswerChoices);
          setTotalPlayersSubmitted(0);
          setPlayersUnsubmitted();
          await sendHostMessage("ShowQuestionComplete");
          break;
        case "TotalPlayersIn":
          setTotalPlayersSubmitted(msg.Payload as number);
          break;
        case "AllGuessesIn":
          setState(msg.Type);
          const results: AllGuessesInType = msg.Payload as AllGuessesInType;
          setReminder(results.Reminder);
          setInstructions(results.Instructions);
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Score: results.Points[p.Name],
                Submitted: p.Name === results.TrueAnswer ? true : p.Submitted,
              };
            })
          );
          setCards([results.TrueAnswer]);
          setTotalPlayersSubmitted(-1);
          await new Promise((resolve) =>
            setTimeout(resolve, delayShowingAnswersMS)
          );
          await sendHostMessage("ShowResultsComplete");
          break;
        case "GameOver":
          setState(msg.Type);
          const gameOver: GameOverType = msg.Payload as GameOverType;
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Score: gameOver.Points[p.Name],
                Submitted: p.Name === results.TrueAnswer ? true : p.Submitted,
              };
            })
          );
          return true;
        default:
          console.log("Unimplemented message:", msg);
          break;
      }
      return false;
    },
    [players, setPlayers, setPlayersUnsubmitted, sendHostMessage]
  );
  // TODO
  useEffect(() => {
    handleMessage({ Type: "TestMessage", Payload: {} } as GTVMessage);
  }, [handleMessage]);
  return (
    <TransitionContainer
      transitionTrigger={state}
      duration={1}
      initiallyVisible={false}
    >
      {state !== "GameOver" ? (
        <MultipleChoice
          theme={theme}
          instructions={instructions}
          totalPlayersSubmitted={totalPlayersSubmitted}
          cards={cards}
          reminder={reminder}
          round={"Round " + round}
        />
      ) : (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "calc(95vh)",
          }}
        >
          <DynamicText style={{ fontSize: 30, marginBottom: 10 }}>
            Game Over!
          </DynamicText>
          <ShowScores players={players} onRenderComplete={async () => {
            await sendHostMessage("ShowResultsComplete");
          }} />
          <PlayerReminder reminder={reminder} theme={theme} />
        </Box>
      )}
    </TransitionContainer>
  );
};

export default TriviaGame;
